import React from "react";
import { Link } from "react-router-dom";
import {
  BadgeCheck,
  Gift,
  Moon,
  Truck,
  Sparkles,
  Briefcase,
  MessageCircle,
  MapPin,
  Calendar,
  BellRing,
  ChevronRight,
  Star,
  Ticket,
  Percent,
  Tag,
} from "lucide-react";
import {
  BRAND,
  TESTIMONIALS,
  WHY_CHOOSE_US,
  FAQ_CATEGORIES,
  waLink,
  formatSGD,
} from "../lib/mockData";
import { useProducts, useCategories, usePromoCodes, useSettings } from "../hooks/useCatalog";
import { isPromoValidNow } from "../lib/promo";
import { useSeo } from "../lib/seo";
import { OrnamentDivider, SectionLabel, CrescentStar } from "../components/common/Ornaments";
import ProductCard from "../components/common/ProductCard";

const ICON_MAP = { BadgeCheck, Gift, Moon, Truck, Sparkles, Briefcase };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// "2026-04-30" -> "30 Apr 2026" (string-parsed to stay timezone-safe)
const formatEndDate = (d) => {
  const [y, m, day] = String(d || "").split("-");
  if (!y || !m || !day) return null;
  const month = MONTHS[Number(m) - 1];
  return month ? `${Number(day)} ${month} ${y}` : null;
};

const promoOffer = (p) =>
  p.type === "Percentage" ? `${p.value}% off` : `${formatSGD(p.value)} off`;

const promoMin = (p) =>
  p.minPurchase > 0 ? `min. ${formatSGD(p.minPurchase)}` : "no min. spend";

export default function Home() {
  const { data: products = [], isLoading: productsLoading } = useProducts();
  const { data: categories = [], isLoading: categoriesLoading } = useCategories();
  const { data: promoCodes = [] } = usePromoCodes();
  const { data: settings } = useSettings();
  const featured = products.filter((p) => p.featured).slice(0, 6);
  const bestSellers = products.filter((p) => p.bestSeller).slice(0, 4);
  const newArrivals = products.filter((p) => p.newArrival).slice(0, 4);
  const onSale = products.filter((p) => p.discountPrice).slice(0, 3);
  // Only advertise codes that are actually redeemable right now (active AND
  // inside their date window AND not used up) — never promote an expired code.
  const activePromos = promoCodes.filter(isPromoValidNow);
  const hasPromotions = onSale.length > 0 || activePromos.length > 0;
  useSeo({ pageKey: "home" });

  // Hero showcase product, chosen explicitly in Admin → Settings. Falls back to
  // the first Featured product, then to a stock photo, so the hero is never
  // blank if the chosen product is unpublished or deleted.
  const heroProduct =
    products.find((p) => p.id === settings?.heroProductId) || featured[0] || null;
  const heroImg =
    heroProduct?.images?.[0] ||
    "https://images.pexels.com/photos/27393960/pexels-photo-27393960.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=900&w=1200";

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 pb-20 md:pt-20 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div className="fade-up">
              <SectionLabel className="justify-start md:justify-start" >
                <span className="mx-0">Singapore Halal Gift Hampers</span>
              </SectionLabel>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-emerald mt-5">
                Give-a-gift that keeps <span className="text-gold italic">relationship closer.</span>
              </h1>
              <div className="mt-6 space-y-4 text-base text-emerald-light/90 leading-relaxed max-w-xl">
                <p>
                  The present life system that is always on-the-move, is the reason why people often
                  do not have time for casual meetings with each other. Hence, the practice of
                  give-a-gift to someone you know, will be very effective in helping to keep good
                  relationships between peoples.
                </p>
                <p>
                  <span className="font-semibold text-emerald">“Halalgifhampers”</span> sells gift
                  packages that fulfil the needs of those who face the challenges to maintain good
                  relationships. Every gift package is guaranteed to be halal-compliant and sold at
                  an affordable price.
                </p>
              </div>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link
                  to="/shop"
                  className="inline-flex items-center gap-2 bg-emerald text-cream px-7 py-3.5 text-sm font-medium hover:bg-emerald-dark transition-colors"
                  data-testid="hero-shop-btn"
                >
                  Shop Hampers
                  <ChevronRight size={16} />
                </Link>
                <a
                  href={waLink("Hi HALALGIFHAMPERS, I'd like to enquire about your hampers.")}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 border border-gold text-emerald px-7 py-3.5 text-sm font-medium hover:bg-gold/10 transition-colors"
                  data-testid="hero-whatsapp-btn"
                >
                  <MessageCircle size={16} />
                  WhatsApp Us
                </a>
              </div>

              {/* Trust row */}
              <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-xs text-emerald-light/80">
                <div className="flex items-center gap-2">
                  <BadgeCheck size={16} className="text-gold" /> Halal-Friendly Selections
                </div>
                <div className="flex items-center gap-2">
                  <Truck size={16} className="text-gold" /> Islandwide SG Delivery
                </div>
                <div className="flex items-center gap-2">
                  <Star size={16} className="text-gold" fill="currentColor" /> Trusted Since Day One
                </div>
              </div>
            </div>

            {/* Hero image */}
            <div className="relative fade-up" style={{ animationDelay: "120ms" }}>
              {/* Links through to the showcased product when one is set. */}
              {React.createElement(
                heroProduct ? Link : "div",
                {
                  // aspect-square + object-contain: product photos are uploaded
                  // square (e.g. 1254x1254), so a 4/5 portrait box with cover was
                  // cropping the left/right edges off. Square box fits them
                  // exactly, and contain guarantees nothing is ever cut off even
                  // if a portrait or landscape photo is uploaded later.
                  className:
                    "block relative aspect-square rounded-2xl overflow-hidden gold-hairline bg-cream-2 shadow-[0_30px_60px_-30px_rgba(0,76,51,0.35)]",
                  ...(heroProduct
                    ? { to: `/product/${heroProduct.slug}`, "data-testid": "hero-product-link" }
                    : {}),
                },
                <>
                  <img
                    src={heroImg}
                    alt={heroProduct?.name || "Premium halal gift hamper"}
                    className="w-full h-full object-contain"
                    data-testid="hero-image"
                  />
                  {/* Stronger than before: with contain, the caption can land on a
                      light letterbox bar, so it needs a solid scrim to stay legible. */}
                  <div className="absolute inset-0 bg-gradient-to-t from-emerald/85 via-emerald/10 to-transparent" />
                  <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-cream">
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.3em] text-gold">Signature</div>
                      <div className="font-serif text-2xl leading-tight mt-1" data-testid="hero-product-name">
                        {heroProduct?.name || "Premium Wedding Hamper"}
                      </div>
                    </div>
                    <CrescentStar className="text-gold" size={36} />
                  </div>
                </>
              )}

              {/* Floating badge */}
              <div className="hidden md:flex absolute -left-6 top-10 bg-cream border border-gold/40 rounded-xl px-5 py-4 shadow-lg items-center gap-3 max-w-xs">
                <div className="w-10 h-10 rounded-full bg-emerald flex items-center justify-center text-gold">
                  <Gift size={18} />
                </div>
                <div>
                  <div className="font-serif text-lg text-emerald leading-tight">200+ Hampers</div>
                  <div className="text-[11px] text-emerald-light/70">Curated for every occasion</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Subtle decorative crescents */}
        <CrescentStar
          className="hidden lg:block absolute top-20 right-10 text-gold/15"
          size={80}
        />
      </section>

      {/* SHOP BY OCCASION */}
      <section className="bg-cream-2/50 py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel>Explore</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-4">
              Shop by <span className="italic text-gold">Occasion</span>
            </h2>
            <p className="text-emerald-light/80 mt-4">
              From weddings and newborn welcomes to festive celebrations and corporate gifting — a halal hamper for every meaningful moment.
            </p>
            <OrnamentDivider className="mt-8 text-gold" />
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5 md:gap-6">
            {categoriesLoading &&
              [...Array(8)].map((_, i) => (
                <div
                  key={i}
                  className="rounded-xl aspect-square bg-emerald/10 animate-pulse"
                />
              ))}
            {!categoriesLoading &&
              categories.map((c, i) => (
              <Link
                key={c.slug}
                to={`/category/${c.slug}`}
                className="group relative overflow-hidden rounded-xl aspect-square bg-emerald fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
                data-testid={`category-card-${c.slug}`}
              >
                <img
                  src={c.image}
                  alt={c.name}
                  className="absolute inset-0 w-full h-full object-cover opacity-85 img-zoom"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-emerald via-emerald/40 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-4 md:p-5 text-cream">
                  <div className="font-serif text-lg md:text-xl leading-tight">{c.name}</div>
                  <div className="text-[11px] md:text-xs text-cream/75 mt-1 line-clamp-1">
                    {c.tagline}
                  </div>
                  <div className="mt-2 inline-flex items-center gap-1 text-gold text-xs font-medium">
                    Shop Now <ChevronRight size={12} />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED HAMPERS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <SectionLabel className="justify-start">Featured</SectionLabel>
              <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-4">
                Signature <span className="italic text-gold">Hampers</span>
              </h2>
              <p className="text-emerald-light/80 mt-2 max-w-xl">
                Our most-loved halal gift hampers — beautifully wrapped, thoughtfully curated.
              </p>
            </div>
            <Link
              to="/shop"
              className="text-sm font-medium text-emerald hover:text-gold transition-colors inline-flex items-center gap-1"
              data-testid="featured-view-all"
            >
              View All Hampers <ChevronRight size={14} />
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {productsLoading
              ? [...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="rounded-xl bg-emerald/10 animate-pulse aspect-[3/4]"
                  />
                ))
              : featured.map((p, i) => (
                  <ProductCard key={p.id} product={p} index={i} />
                ))}
          </div>
        </div>
      </section>

      {/* BEST SELLERS */}
      {(productsLoading || bestSellers.length > 0) && (
        <section className="bg-cream-2/50 py-16 md:py-24" data-testid="home-bestsellers">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <SectionLabel className="justify-start">Best Sellers</SectionLabel>
                <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-4">
                  Most <span className="italic text-gold">Loved</span>
                </h2>
                <p className="text-emerald-light/80 mt-2 max-w-xl">
                  The halal hampers our customers return to again and again — tried, gifted and adored.
                </p>
              </div>
              <Link
                to="/shop?sort=popular"
                className="text-sm font-medium text-emerald hover:text-gold transition-colors inline-flex items-center gap-1"
                data-testid="bestsellers-view-all"
              >
                View All Best Sellers <ChevronRight size={14} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productsLoading
                ? [...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-emerald/10 animate-pulse aspect-[3/4]"
                    />
                  ))
                : bestSellers.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* NEW ARRIVALS */}
      {(productsLoading || newArrivals.length > 0) && (
        <section className="py-16 md:py-24" data-testid="home-new-arrivals">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
              <div>
                <SectionLabel className="justify-start">New Arrivals</SectionLabel>
                <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-4">
                  Fresh from the <span className="italic text-gold">Atelier</span>
                </h2>
                <p className="text-emerald-light/80 mt-2 max-w-xl">
                  Our newest halal hamper creations — just curated, ready to gift.
                </p>
              </div>
              <Link
                to="/shop"
                className="text-sm font-medium text-emerald hover:text-gold transition-colors inline-flex items-center gap-1"
                data-testid="new-arrivals-view-all"
              >
                View All Hampers <ChevronRight size={14} />
              </Link>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {productsLoading
                ? [...Array(4)].map((_, i) => (
                    <div
                      key={i}
                      className="rounded-xl bg-emerald/10 animate-pulse aspect-[3/4]"
                    />
                  ))
                : newArrivals.map((p, i) => (
                    <ProductCard key={p.id} product={p} index={i} />
                  ))}
            </div>
          </div>
        </section>
      )}

      {/* PROMOTIONAL OFFERS */}
      {(productsLoading || hasPromotions) && (
        <section className="py-16 md:py-24" data-testid="home-promotions">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative overflow-hidden rounded-2xl bg-emerald text-cream p-8 md:p-12">
              <div className="absolute inset-0 opacity-[0.06] islamic-pattern pointer-events-none" />
              <CrescentStar className="absolute -top-8 -right-8 text-gold/15" size={160} />

              <div className="relative">
                <div className="text-center max-w-2xl mx-auto">
                  <SectionLabel>Special Offers</SectionLabel>
                  <h2 className="font-serif text-4xl md:text-5xl mt-4">
                    Promotional <span className="italic text-gold">Offers</span>
                  </h2>
                  <p className="text-cream/75 mt-4">
                    Limited-time savings on selected halal hampers — plus promo codes you can apply at checkout.
                  </p>
                  <OrnamentDivider className="mt-8 text-gold" />
                </div>

                {/* On sale now */}
                {productsLoading ? (
                  <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {[...Array(3)].map((_, i) => (
                      <div
                        key={i}
                        className="rounded-xl h-36 bg-cream/10 animate-pulse"
                      />
                    ))}
                  </div>
                ) : (
                  onSale.length > 0 && (
                    <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
                      {onSale.map((p, i) => (
                        <Link
                          key={p.id}
                          to={`/product/${p.slug}`}
                          className="group flex items-center gap-4 rounded-xl border border-gold/25 bg-emerald-dark/40 p-4 hover:border-gold/60 transition-colors fade-up"
                          style={{ animationDelay: `${i * 70}ms` }}
                          data-testid={`promo-product-${p.id}`}
                        >
                          <div className="w-20 h-24 shrink-0 rounded-lg overflow-hidden bg-emerald-dark">
                            <img
                              src={p.images?.[0]}
                              alt={p.name}
                              className="w-full h-full object-contain"
                              loading="lazy"
                            />
                          </div>
                          <div className="min-w-0">
                            <div className="font-serif text-lg leading-tight text-cream group-hover:text-gold transition-colors line-clamp-2">
                              {p.name}
                            </div>
                            <div className="mt-2 flex items-baseline gap-2">
                              <span className="text-lg font-semibold text-gold">
                                {formatSGD(p.discountPrice)}
                              </span>
                              <span className="text-xs text-cream/55 line-through">
                                {formatSGD(p.price)}
                              </span>
                            </div>
                            <span className="mt-2 inline-block rounded-full bg-gold text-emerald text-[10px] px-2.5 py-1 uppercase tracking-widest font-semibold">
                              Save {formatSGD(p.price - p.discountPrice)}
                            </span>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )
                )}

                {/* Active promo codes */}
                {activePromos.length > 0 && (
                  <div className="mt-10 pt-8 border-t border-gold/20">
                    <div className="flex items-center gap-2 text-gold text-xs uppercase tracking-[0.28em] font-medium">
                      <Ticket size={14} strokeWidth={1.7} /> Promo Codes
                    </div>

                    <div className="mt-5 flex flex-wrap gap-3">
                      {activePromos.map((promo, i) => (
                        <div
                          key={promo.code}
                          className="flex items-center gap-3 rounded-lg border border-dashed border-gold/50 bg-gold/10 px-4 py-3 fade-up"
                          style={{ animationDelay: `${i * 60}ms` }}
                          data-testid={`promo-code-${promo.code}`}
                        >
                          <div className="w-9 h-9 shrink-0 rounded-md bg-gold text-emerald flex items-center justify-center">
                            {promo.type === "Percentage" ? (
                              <Percent size={16} strokeWidth={2} />
                            ) : (
                              <Tag size={16} strokeWidth={2} />
                            )}
                          </div>
                          <div>
                            <div className="font-mono text-sm font-semibold tracking-[0.18em] text-gold">
                              {promo.code}
                            </div>
                            <div className="text-xs text-cream/75 mt-0.5">
                              {promoOffer(promo)} · {promoMin(promo)}
                            </div>
                            {formatEndDate(promo.end) && (
                              <div className="text-[10px] text-cream/50 mt-0.5">
                                Valid till {formatEndDate(promo.end)}
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <p className="mt-5 text-xs text-cream/60">
                      Apply your code at checkout. Offers valid while stocks last; terms apply.
                    </p>
                  </div>
                )}

                <div className="mt-10 flex flex-wrap gap-3">
                  <Link
                    to="/shop"
                    className="inline-flex items-center gap-2 bg-gold text-emerald px-6 py-3 text-sm font-semibold hover:bg-gold-light transition-colors"
                    data-testid="promotions-shop-btn"
                  >
                    Shop the Offers <ChevronRight size={16} />
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* WHY CHOOSE US */}
      <section className="bg-emerald text-cream py-16 md:py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.06] islamic-pattern pointer-events-none" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <div className="flex items-center justify-center gap-3 text-gold text-xs uppercase tracking-[0.28em]">
              <span className="h-px w-8 bg-gold/60" /> Why Choose Us <span className="h-px w-8 bg-gold/60" />
            </div>
            <h2 className="font-serif text-4xl md:text-5xl mt-4">
              The <span className="italic text-gold">HALALGIFHAMPERS</span> Difference
            </h2>
          </div>

          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {WHY_CHOOSE_US.map((f, i) => {
              const Icon = ICON_MAP[f.icon];
              return (
                <div
                  key={i}
                  className="border border-gold/20 p-6 rounded-xl bg-emerald-dark/40 fade-up"
                  style={{ animationDelay: `${i * 70}ms` }}
                >
                  <div className="w-12 h-12 rounded-lg bg-gold/15 text-gold flex items-center justify-center">
                    <Icon size={22} strokeWidth={1.6} />
                  </div>
                  <div className="font-serif text-2xl mt-4 text-cream">{f.title}</div>
                  <div className="text-sm text-cream/75 mt-2 leading-relaxed">{f.text}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CORPORATE + CUSTOM */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Corporate */}
          <div className="relative overflow-hidden rounded-2xl bg-emerald text-cream p-8 md:p-12">
            <CrescentStar className="absolute -top-6 -right-6 text-gold/15" size={140} />
            <SectionLabel className="justify-start"><span>For Business</span></SectionLabel>
            <h3 className="font-serif text-3xl md:text-4xl mt-4">
              Corporate Hampers & <span className="italic text-gold">Bulk Gift Orders</span>
            </h3>
            <p className="mt-4 text-cream/80 leading-relaxed">
              For companies looking to send halal-friendly hampers to clients, partners, employees, or event guests — HALALGIFHAMPERS offers elegant gifting options suitable for corporate appreciation, festive seasons, business openings, and client relationship building.
            </p>
            <a
              href={waLink("Hi HALALGIFHAMPERS, I'd like to enquire about corporate bulk hampers.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-gold text-emerald px-6 py-3 text-sm font-semibold hover:bg-gold-light transition-colors"
              data-testid="corporate-whatsapp-btn"
            >
              <MessageCircle size={16} /> Enquire on WhatsApp
            </a>
          </div>

          {/* Custom */}
          <div className="relative overflow-hidden rounded-2xl border border-gold/40 bg-cream p-8 md:p-12">
            <SectionLabel className="justify-start">Bespoke</SectionLabel>
            <h3 className="font-serif text-3xl md:text-4xl mt-4 text-emerald">
              Need a <span className="italic text-gold">Custom Hamper?</span>
            </h3>
            <p className="mt-4 text-emerald-light/85 leading-relaxed">
              Tell us your occasion, budget, recipient profile, and preferred items. We'll help curate a suitable halal gift hamper tailored to your story.
            </p>
            <a
              href={waLink("Hi HALALGIFHAMPERS, I'd like a custom hamper.")}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-6 inline-flex items-center gap-2 bg-emerald text-cream px-6 py-3 text-sm font-medium hover:bg-emerald-dark transition-colors"
              data-testid="custom-whatsapp-btn"
            >
              <MessageCircle size={16} /> Customise via WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* DELIVERY INFO */}
      <section className="bg-cream-2/60 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel>Delivery</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-3">
              Reliable, <span className="italic text-gold">Islandwide</span>
            </h2>
          </div>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: MapPin, title: "Islandwide Delivery", text: "We deliver to every corner of Singapore." },
              { icon: Calendar, title: "Preferred Delivery Date", text: "Choose your ideal date at checkout." },
              { icon: BellRing, title: "Delivery Status Updates", text: "Stay informed at every step." },
              { icon: Gift, title: "Gift Message Included", text: "Add a handwritten personal note." },
            ].map((f, i) => (
              <div
                key={i}
                className="bg-white p-6 rounded-xl border border-gold/25 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="w-11 h-11 rounded-lg bg-emerald text-gold flex items-center justify-center">
                  <f.icon size={20} strokeWidth={1.6} />
                </div>
                <div className="font-serif text-xl mt-4 text-emerald">{f.title}</div>
                <div className="text-sm text-emerald-light/80 mt-1.5">{f.text}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto">
            <SectionLabel>Kind Words</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-3">
              Loved by <span className="italic text-gold">Our Community</span>
            </h2>
          </div>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <figure
                key={i}
                className="bg-white p-6 rounded-xl border border-gold/25 fade-up"
                style={{ animationDelay: `${i * 60}ms` }}
              >
                <div className="flex gap-0.5 text-gold">
                  {[...Array(5)].map((_, j) => (
                    <Star key={j} size={14} fill="currentColor" strokeWidth={0} />
                  ))}
                </div>
                <blockquote className="mt-4 text-sm text-emerald-light/90 leading-relaxed">
                  “{t.text}”
                </blockquote>
                <figcaption className="mt-5 pt-4 border-t border-gold/20">
                  <div className="font-serif text-lg text-emerald leading-tight">{t.name}</div>
                  <div className="text-xs text-emerald-light/70">{t.role}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ PREVIEW */}
      <section className="bg-cream-2/50 py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <SectionLabel>Quick Answers</SectionLabel>
            <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-3">
              Frequently <span className="italic text-gold">Asked</span>
            </h2>
          </div>
          <div className="mt-10 space-y-3">
            {FAQ_CATEGORIES.slice(0, 4).flatMap((c) => c.items).slice(0, 4).map((f, i) => (
              <details
                key={i}
                className="group bg-white rounded-xl border border-gold/25 p-5 open:shadow-md transition-shadow"
                data-testid={`faq-item-${i}`}
              >
                <summary className="flex items-center justify-between gap-4 cursor-pointer list-none">
                  <span className="font-serif text-lg text-emerald">{f.q}</span>
                  <ChevronRight
                    size={18}
                    className="text-gold transition-transform group-open:rotate-90"
                  />
                </summary>
                <p className="mt-3 text-sm text-emerald-light/85 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              to="/faq"
              className="text-sm text-emerald hover:text-gold transition-colors inline-flex items-center gap-1"
            >
              View all FAQs <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-20 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <CrescentStar className="mx-auto text-gold" size={40} />
          <h2 className="font-serif text-4xl md:text-5xl text-emerald mt-6">
            Gift with <span className="italic text-gold">grace</span>.
          </h2>
          <p className="mt-4 text-emerald-light/85 max-w-xl mx-auto">
            Whether it's a wedding, a newborn, a festive season or a corporate milestone — let HALALGIFHAMPERS handle the details, beautifully.
          </p>
          <div className="mt-8 flex flex-wrap justify-center gap-3">
            <Link
              to="/shop"
              className="inline-flex items-center gap-2 bg-emerald text-cream px-7 py-3.5 text-sm font-medium hover:bg-emerald-dark transition-colors"
            >
              Browse Hampers <ChevronRight size={16} />
            </Link>
            <a
              href={waLink(`Hi ${BRAND.name}, I'd like to enquire.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 border border-gold text-emerald px-7 py-3.5 text-sm font-medium hover:bg-gold/10 transition-colors"
            >
              <MessageCircle size={16} /> Chat on WhatsApp
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
